import EFVideoModal from '@/components/UI/EFVideoModal';
import Image from '@/components/UI/Image';
import EFFileIcon from '@/components/UI/svg/Support/EFFileIcon';
import EFCollapse from '@/efelement/EFCollapse';
import generatorPlayUrl from '@/utils/generatorPlayUrl';
import { useGlobal } from '@/utils/useGlobal';
import { useEffect, useMemo, useState } from 'react';
import type { SupportMenu } from '.';
import { SUPPORT_MENU_KEYS } from '.';
import css from './index.module.scss';
export type FAQ = {
    typeTag: 'PRODUCT_RELATED' | 'NOTE' | 'FAULT';
    faqInfoId: string;
    title: string;
    description: string;
};

export type Download = {
    downloadInfoId: string;
    fileUrl: string;
    title: string;
    classify: 'MANUAL' | 'VIDEO_COURSE' | 'ONLINE' | '';
    updateTime: string;
    poster: string;
};

export type SupportContentPorps = {
    selectedkey: string;
    theme: 'black' | '';
    allMenus: SupportMenu;
    supportData: { faqList: FAQ[]; downloadList: Download[]; videoList: Download[]; onlineList: Download[] };
};

export default function SupportContent({ selectedkey, theme, allMenus, supportData: { faqList, downloadList, videoList, onlineList } }: SupportContentPorps) {
    const { isMobile } = useGlobal();
    const [faqMap, setFaqMap] = useState<Record<string, FAQ[]>>({
        [SUPPORT_MENU_KEYS.INTRODUCTION]: [],
        [SUPPORT_MENU_KEYS.TROUBLESHOOTING]: [],
        [SUPPORT_MENU_KEYS.NOTE]: [],
    });
    const title = useMemo(() => allMenus?.find((menu) => menu.key === selectedkey)?.label, [allMenus, selectedkey]);

    useEffect(() => {
        const faqs = faqList.reduce(
            (acc, faq) => {
                const key = faq.typeTag.toLowerCase();
                acc[key].push(faq);
                return acc;
            },
            {
                product_related: [],
                fault: [],
                note: [],
            } as Record<string, FAQ[]>,
        );
        setFaqMap({
            [SUPPORT_MENU_KEYS.INTRODUCTION]: faqs.product_related,
            [SUPPORT_MENU_KEYS.TROUBLESHOOTING]: faqs.fault,
            [SUPPORT_MENU_KEYS.NOTE]: faqs.note,
        });
    }, [faqList]);

    return (
        <>
            {!isMobile && <h2 className="title">{title}</h2>}
            {[SUPPORT_MENU_KEYS.INTRODUCTION, SUPPORT_MENU_KEYS.TROUBLESHOOTING, SUPPORT_MENU_KEYS.NOTE].includes(selectedkey) && (
                <SupportFaq faqList={faqMap[selectedkey] || []} theme={theme} />
            )}
            {selectedkey === SUPPORT_MENU_KEYS.TUTORIAL && <SupportTutorial videoList={videoList} theme={theme} />}
            {selectedkey === SUPPORT_MENU_KEYS.DOWNLOADS && (
                <SupportDownload downloadList={downloadList?.filter((list: any) => ['MANUAL', 'ONLINE'].includes(list?.classify))} theme={theme} />
            )}
            {selectedkey === SUPPORT_MENU_KEYS.ONLINE && <SupportDownload downloadList={onlineList} theme={theme} />}
        </>
    );
}

function SupportFaq({ faqList, theme }: { faqList: FAQ[]; theme: string }) {
    const { isMobile } = useGlobal();
    return (
        <div className={`${css.faq_content} ${theme}`}>
            <EFCollapse data={faqList} theme={theme === 'black' ? 'dark' : 'light'} className={`${isMobile ? 'mobile' : ''}`} />
        </div>
    );
}

function SupportTutorial({ videoList, theme }: { videoList: Download[]; theme: string }) {
    const { useLang, useRouter, isMobile } = useGlobal();
    const { locale } = useRouter();
    const { t } = useLang('shared');

    const [videoModal, setVideoModal] = useState(false);
    const [videoUrl, setVideoUrl] = useState('');

    /**
     * 点击播放视频
     */
    const handlePlayVideo = (e: any) => {
        const target = e.target?.closest('.mask');
        if (!target?.classList.contains('play')) return;
        const { url, isYoutube } = target.dataset;
        if (isYoutube === 'true') {
            setVideoUrl(url);
            setVideoModal(true);
        } else {
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            link.click();
        }
    };

    return (
        <>
            <ul className={`${css.videoUL} ${theme} video-ul ${isMobile ? 'video-ul-h5' : 'video-ul-pc'}`} onClick={handlePlayVideo}>
                {videoList?.map((videoItem) => (
                    <li className={`videoItem ${isMobile ? 'mobile' : ''}`} key={videoItem.downloadInfoId + videoItem.fileUrl}>
                        <div className="videos">
                            <div
                                data-url={generatorPlayUrl(videoItem.fileUrl).url}
                                data-is-youtube={generatorPlayUrl(videoItem.fileUrl).isYouTubeVideo}
                                className="play mask"
                            >
                                <Image
                                    src={videoItem?.poster || generatorPlayUrl(videoItem.fileUrl)?.imageUrl || '/img/search/error.png'}
                                    layout="fill"
                                    alt={videoItem.title}
                                />
                                <div className="play">
                                    <Image src="/icons/play-support.svg" layout="fill" alt={t('support.download.play_icon')} />
                                </div>
                            </div>
                        </div>
                        <p data-url={videoItem.fileUrl} className="video_title">
                            {videoItem.title}
                        </p>
                    </li>
                ))}
            </ul>
            <EFVideoModal visible={videoModal} source={videoUrl} onClose={() => setVideoModal(false)} playerType={locale === 'cn' ? 'Alibaba' : 'YouTube'} />
        </>
    );
}

function SupportDownload({ downloadList: fileList, theme }: { downloadList: Download[]; theme: string }) {
    const { Link, isMobile, useLang } = useGlobal();
    const { t } = useLang('shared.product');
    return (
        <ul className={`${css.file_list} ${theme}`}>
            {Array.isArray(fileList) &&
                fileList?.length > 0 &&
                fileList?.map((file) => (
                    <li className={`fileItem ${isMobile ? 'mobile' : ''}`} key={file.downloadInfoId}>
                        <div className="left">
                            <div className="file-icon">
                                <EFFileIcon fill={theme === 'black' ? '#fff' : '#000'} />
                            </div>
                            <div className="file-content">
                                <p className="desc font-family-medium">{file.title}</p>
                                <p className="time font-family-medium">{file.updateTime}</p>
                            </div>
                        </div>
                        <Link href={file.fileUrl} ariaLabel={'Download PDF'} className="download">
                            <span className="download-btn font-family-semibold">
                                {file.classify === 'ONLINE' ? <OnlineIcon /> : <span className="pdfDownload"></span>}
                                {file.classify === 'ONLINE' ? t('view') : 'PDF'}
                                {/* <span className="pdfDownload"></span>PDF */}
                            </span>
                        </Link>
                    </li>
                ))}
        </ul>
    );
}

function OnlineIcon() {
    return (
        <svg width="18" height="18" style={{ marginRight: '4px' }} viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
            <g stroke="currentColor" strokeWidth=".2" fill="currentColor" fillRule="nonzero">
                <path d="M15.031 8.001a.616.616 0 0 0-.019-.163.795.795 0 0 0-.03-.158l-.012-.03a.488.488 0 0 0-.024-.064C13.65 4.75 10.858 2.844 8 2.844c-2.857 0-5.649 1.904-6.94 4.725a.47.47 0 0 0-.033.086.813.813 0 0 0-.01.023c-.025.075-.026.127-.023.114a1.916 1.916 0 0 0-.023.163.406.406 0 0 0 0 .093s.01.095.016.114a.785.785 0 0 0 .016.102.47.47 0 0 0 .029.09.544.544 0 0 0 .022.059C2.352 11.25 5.144 13.155 8 13.155c2.858 0 5.65-1.905 6.932-4.712a.494.494 0 0 0 .042-.101.46.46 0 0 0 .035-.148c.008-.045.022-.113.022-.194zm-.947.035-.002.007-.006.02C12.922 10.55 10.486 12.22 8 12.22c-2.48 0-4.912-1.664-6.077-4.16l-.008-.025c0-.008-.002-.015-.003-.02l-.002-.018V7.99a.331.331 0 0 0 .006-.032.948.948 0 0 0 .005-.019C3.076 5.451 5.514 3.781 8 3.781c2.487 0 4.925 1.671 6.075 4.156l.004.012.004.013a.512.512 0 0 0 .007.046l-.006.028z"></path>
                <path d="M8 5.656A2.346 2.346 0 0 0 5.656 8 2.346 2.346 0 0 0 8 10.344 2.346 2.346 0 0 0 10.344 8 2.346 2.346 0 0 0 8 5.656zm0 3.75c-.775 0-1.406-.63-1.406-1.406 0-.775.63-1.406 1.406-1.406.775 0 1.406.63 1.406 1.406 0 .775-.63 1.406-1.406 1.406z"></path>
            </g>
        </svg>
    );
}
